import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';

const items: TabContent['items'] = [
  {
    position: { x: 0, width: 12, y: 0, height: 8 },
    id: 'role-composition-v3-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 12, width: 12, y: 0, height: 8 },
    id: 'role-composition-v3-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 24, width: 12, y: 0, height: 8 },
    id: 'role-composition-v3-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 36, width: 12, y: 0, height: 8 },
    id: 'role-composition-v3-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 0, y: 8, width: 24, height: 28 },
    id: 'role-composition-v3-roles-taxonomy',
    title: 'Taxonomy',
    isTitleManuallySet: true,
    info: {
      text: 'The selected role and up to two levels of its breakdown beneath it.',
    },
    plot: {
      chartType: 'icicle',
      primaryEntitySwitcher: true,
      secondaryDimension: 'role',
      defaultMetricOptions: ['headcount'],
      treemap: {
        taxonomyCoverage: {
          topN: { groups: 10, cells: 50 },
          omitOther: true,
        },
      },
      icicle: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 24, y: 8, width: 24, height: 14 },
    id: 'role-composition-v3-skills-treemap',
    title: 'Skills',
    isTitleManuallySet: true,
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      hideOther: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 24, y: 22, width: 24, height: 14 },
    id: 'role-composition-v3-activities',
    title: 'Activities',
    isTitleManuallySet: true,
    plot: {
      secondaryDimension: 'activities',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      hideOther: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
];

const roleCompositionTab: TabContent = {
  id: 'role-composition-v3',
  items,
  primaryFilter: {
    id: FilterId.ROLE,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.SNAPSHOT_DATE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.GEOGRAPHY,
    FilterId.CUSTOM_SKILL,
    FilterId.ACTIVITY,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
};

export const roleComposition: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.ROLE_COMPOSITION,
  revelioPageId: RevelioPageId.ROLE_COMPOSITION,
  name: 'Overview',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'role-composition-v3',
      label: 'Catalog',
      tabContent: roleCompositionTab,
      isActive: true,
    },
  ],
};
